/*
 * Layout de login padrão (React) — design system Scorvia.
 * Estilos em login/login.css (importe uma vez no app). Só apresentação:
 * estado de autenticação, validação e rotas ficam no projeto.
 *
 * Uso:
 *   <LoginLayout brand={...} badge="..." title="..." highlight="..." lead="..." steps={[...]}
 *                heading="Bem-vindo de volta" subtitle="..." help={...} footer="...">
 *     <form className="ds-login__form" onSubmit={...} noValidate>
 *       <LoginField ... />
 *       <LoginPasswordField ... labelAside={<Link className={loginLinkClass} ...>Esqueci minha senha</Link>} />
 *       <LoginSubmit loading={...}>Entrar</LoginSubmit>
 *     </form>
 *   </LoginLayout>
 */
import {
  forwardRef,
  useId,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'
import { BrandMark, scorviaBrand, type Brand } from '../brand/BrandMark'

export const loginLinkClass = 'ds-login__link'

/* ---------- ícones internos (sem dependência de biblioteca) ---------- */

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
}

export function LoginAlertIcon() {
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )
}

function EyeIcon({ off }: { off: boolean }) {
  return off ? (
    <svg {...svgProps}>
      <path d="M10.7 5.1A10.4 10.4 0 0 1 12 5c7 0 10 7 10 7a13.2 13.2 0 0 1-1.7 2.7" />
      <path d="M6.6 6.6A13.5 13.5 0 0 0 2 12s3 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  ) : (
    <svg {...svgProps}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg {...svgProps} className="ds-login__submit-arrow">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  )
}

/* ---------- marca: mesmo componente no painel e no topo do formulário (brand/BrandMark) ---------- */

export type LoginBrand = Brand
export { BrandMark, scorviaBrand }

/* ---------- layout ---------- */

export interface LoginStep {
  icon: ReactNode
  title: string
  description: string
}

export interface LoginLayoutProps {
  brand: LoginBrand
  /** Selo opcional no painel (ex.: "Dados da API pública do PNCP"). */
  badge?: ReactNode
  /** Título do painel; a primeira ocorrência de `highlight` recebe a cor da marca. */
  title: string
  highlight?: string | undefined
  lead?: ReactNode
  steps?: LoginStep[] | undefined
  /** Rótulo acessível do painel institucional. */
  panelLabel?: string | undefined
  heading: ReactNode
  subtitle?: ReactNode
  /** Selo de contexto opcional abaixo do subtítulo (ex.: "Acesso restrito a servidores"). */
  context?: ReactNode
  contextIcon?: ReactNode
  /** Alerta geral exibido acima do formulário (ex.: credenciais inválidas). */
  alert?: ReactNode
  help?: ReactNode
  footer?: ReactNode
  /** Conteúdo no rodapé do painel institucional (ex.: selos de segurança). */
  panelFooter?: ReactNode
  /** Link "pular para o formulário", visível só ao receber foco. */
  skipLink?: { href: string; label: string } | undefined
  children: ReactNode
}

function Title({ title, highlight }: { title: string; highlight?: string | undefined }) {
  const i = highlight ? title.indexOf(highlight) : -1
  if (!highlight || i < 0) return <>{title}</>
  return (
    <>
      {title.slice(0, i)}
      <em>{highlight}</em>
      {title.slice(i + highlight.length)}
    </>
  )
}

export function LoginLayout(props: LoginLayoutProps) {
  const {
    brand, badge, title, highlight, lead, steps, panelLabel = 'Sobre o sistema',
    heading, subtitle, context, contextIcon, alert, help, footer, panelFooter, skipLink, children,
  } = props

  return (
    <div className="ds-login">
      {skipLink && (
        <a className="ds-login__skip" href={skipLink.href}>
          {skipLink.label}
        </a>
      )}
      <aside className="ds-login__panel" aria-label={panelLabel}>
        <BrandMark brand={brand} variant="inverse" />
        <div className="ds-login__panel-body">
          {badge && (
            <span className="ds-login__badge">
              <span className="ds-login__badge-dot" aria-hidden="true" />
              {badge}
            </span>
          )}
          <h2 className="ds-login__title">
            <Title title={title} highlight={highlight} />
          </h2>
          {lead && <p className="ds-login__lead">{lead}</p>}
          {steps && steps.length > 0 && (
            <ol className="ds-login__steps">
              {steps.map((step) => (
                <li className="ds-login__step" key={step.title}>
                  <span className="ds-login__step-icon" aria-hidden="true">
                    {step.icon}
                  </span>
                  <span>
                    <strong className="ds-login__step-title">{step.title}</strong>
                    <span className="ds-login__step-text">{step.description}</span>
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
        {panelFooter && <div className="ds-login__trust">{panelFooter}</div>}
      </aside>

      <main className="ds-login__main">
        <div className="ds-login__card">
          <div className="ds-login__mobile-brand">
            <BrandMark brand={brand} />
          </div>
          <h1 className="ds-login__heading">{heading}</h1>
          {subtitle && <p className="ds-login__subtitle">{subtitle}</p>}
          {context && (
            <span className="ds-login__context">
              {contextIcon}
              {context}
            </span>
          )}
          <div className="ds-login__body">
            {alert && <LoginAlert>{alert}</LoginAlert>}
            {children}
          </div>
          {help}
        </div>
        {footer && <p className="ds-login__footer">{footer}</p>}
      </main>
    </div>
  )
}

/* ---------- peças do formulário ---------- */

function CheckIcon() {
  return (
    <svg {...svgProps}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12.5 11 15.5 16 9.5" />
    </svg>
  )
}

export interface LoginAlertProps {
  /** danger: erro (role=alert) · success: confirmação · info: aviso neutro (role=status) */
  variant?: 'danger' | 'success' | 'info' | undefined
  children: ReactNode
}

export function LoginAlert({ variant = 'danger', children }: LoginAlertProps) {
  const cls = variant === 'danger' ? 'ds-login__alert' : `ds-login__alert ds-login__alert--${variant}`
  return (
    <div className={cls} role={variant === 'danger' ? 'alert' : 'status'}>
      {variant === 'success' ? <CheckIcon /> : <LoginAlertIcon />}
      <span>{children}</span>
    </div>
  )
}

/* ---------- select, checkbox e botão secundário ---------- */

export interface LoginSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'className'> {
  id: string
  label: ReactNode
  error?: ReactNode
  children: ReactNode
}

/** Select nativo com o mesmo visual dos campos (seta desenhada em CSS, só com tokens). */
export function LoginSelect({ id, label, error, children, ...selectProps }: LoginSelectProps) {
  const errorId = useId()
  return (
    <div className="ds-login__field">
      <div className="ds-login__label-row">
        <label className="ds-login__label" htmlFor={id}>
          {label}
        </label>
      </div>
      <div className="ds-login__control ds-login__control--select">
        <select
          {...selectProps}
          id={id}
          className="ds-login__input ds-login__select"
          aria-invalid={error ? true : selectProps['aria-invalid']}
          aria-describedby={error ? errorId : selectProps['aria-describedby']}
        >
          {children}
        </select>
      </div>
      {error && (
        <p className="ds-login__message" id={errorId} role="alert">
          <LoginAlertIcon />
          {error}
        </p>
      )}
    </div>
  )
}

export interface LoginCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className' | 'children'> {
  /** Texto do aceite; pode conter links (use loginLinkClass). */
  children: ReactNode
  error?: ReactNode
}

export function LoginCheckbox({ children, error, ...inputProps }: LoginCheckboxProps) {
  const errorId = useId()
  return (
    <div className="ds-login__field">
      <label className="ds-login__check">
        <input
          {...inputProps}
          type="checkbox"
          aria-invalid={error ? true : inputProps['aria-invalid']}
          aria-describedby={error ? errorId : inputProps['aria-describedby']}
        />
        <span>{children}</span>
      </label>
      {error && (
        <p className="ds-login__message" id={errorId} role="alert">
          <LoginAlertIcon />
          {error}
        </p>
      )}
    </div>
  )
}

export interface LoginButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  /** secondary: botão contornado de largura total · link: botão com aparência de link */
  variant?: 'secondary' | 'link' | undefined
}

/** Ações secundárias ("Voltar ao login", "Reenviar e-mail"). Para a ação principal use LoginSubmit. */
export function LoginButton({ variant = 'secondary', type = 'button', ...buttonProps }: LoginButtonProps) {
  return <button {...buttonProps} type={type} className={variant === 'link' ? 'ds-login__link' : 'ds-login__secondary'} />
}

export interface LoginFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'> {
  id: string
  label: ReactNode
  icon?: ReactNode
  /** Mensagem de erro do campo; marca aria-invalid e liga via aria-describedby. */
  error?: ReactNode
  /** Dica exibida abaixo do campo quando não há erro (ex.: "Senhas coincidem"). */
  hint?: ReactNode
  /** Conteúdo extra abaixo do campo (ex.: <LoginStrength />). */
  extra?: ReactNode
  /** Conteúdo à direita do label (ex.: link "Esqueci minha senha"). */
  labelAside?: ReactNode
  variant?: 'default' | 'code' | undefined
}

export const LoginField = forwardRef<HTMLInputElement, LoginFieldProps>(function LoginField(
  { id, label, icon, error, hint, extra, labelAside, variant = 'default', ...inputProps },
  ref,
) {
  return <FieldBase ref={ref} id={id} label={label} icon={icon} error={error} hint={hint} extra={extra} labelAside={labelAside} variant={variant} inputProps={inputProps} />
})

export type LoginPasswordFieldProps = Omit<LoginFieldProps, 'type' | 'variant'> & {
  showLabel?: string | undefined
  hideLabel?: string | undefined
  /** Se informado, exibe este aviso enquanto o Caps Lock estiver ativo no campo. */
  capsLockWarning?: string | undefined
}

export const LoginPasswordField = forwardRef<HTMLInputElement, LoginPasswordFieldProps>(function LoginPasswordField(
  { id, label, icon, error, hint, extra, labelAside, showLabel = 'Mostrar senha', hideLabel = 'Ocultar senha', capsLockWarning, ...inputProps },
  ref,
) {
  const [visible, setVisible] = useState(false)
  const [capsLock, setCapsLock] = useState(false)
  const { onKeyDown, onKeyUp, onBlur } = inputProps
  const checkCaps = (e: KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState('CapsLock'))

  return (
    <FieldBase
      ref={ref}
      id={id}
      label={label}
      icon={icon}
      error={error}
      hint={hint}
      extra={extra}
      warning={capsLockWarning && capsLock ? capsLockWarning : undefined}
      labelAside={labelAside}
      variant="default"
      inputProps={{
        ...inputProps,
        type: visible ? 'text' : 'password',
        onKeyDown: capsLockWarning ? (e) => { checkCaps(e); onKeyDown?.(e) } : onKeyDown,
        onKeyUp: capsLockWarning ? (e) => { checkCaps(e); onKeyUp?.(e) } : onKeyUp,
        onBlur: capsLockWarning ? (e) => { setCapsLock(false); onBlur?.(e) } : onBlur,
      }}
      toggle={
        <button
          type="button"
          className="ds-login__toggle"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          aria-controls={id}
        >
          <EyeIcon off={visible} />
        </button>
      }
    />
  )
})

interface FieldBaseProps {
  id: string
  label: ReactNode
  icon?: ReactNode
  error?: ReactNode
  hint?: ReactNode
  extra?: ReactNode
  /** Aviso não bloqueante (ex.: Caps Lock), anunciado com role="status". */
  warning?: ReactNode
  labelAside?: ReactNode
  variant: 'default' | 'code'
  inputProps: InputHTMLAttributes<HTMLInputElement>
  toggle?: ReactNode
}

const FieldBase = forwardRef<HTMLInputElement, FieldBaseProps>(function FieldBase(
  { id, label, icon, error, hint, extra, warning, labelAside, variant, inputProps, toggle },
  ref,
) {
  const errorId = useId()
  const warningId = useId()
  const hintId = useId()
  const describedBy =
    [inputProps['aria-describedby'], warning ? warningId : undefined, error ? errorId : hint ? hintId : undefined]
      .filter(Boolean)
      .join(' ') || undefined
  const inputClass = [
    'ds-login__input',
    icon ? 'ds-login__input--with-icon' : '',
    toggle ? 'ds-login__input--with-toggle' : '',
    variant === 'code' ? 'ds-login__input--code' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className="ds-login__field">
      <div className="ds-login__label-row">
        <label className="ds-login__label" htmlFor={id}>
          {label}
        </label>
        {labelAside}
      </div>
      <div className="ds-login__control">
        {icon && (
          <span className="ds-login__control-icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <input
          {...inputProps}
          ref={ref}
          id={id}
          className={inputClass}
          aria-invalid={error ? true : inputProps['aria-invalid']}
          aria-describedby={describedBy}
        />
        {toggle}
      </div>
      {extra}
      {!error && hint && (
        <p className="ds-login__message ds-login__message--hint" id={hintId}>
          {hint}
        </p>
      )}
      {warning && (
        <p className="ds-login__message ds-login__message--warning" id={warningId} role="status">
          <LoginAlertIcon />
          {warning}
        </p>
      )}
      {error && (
        <p className="ds-login__message" id={errorId} role="alert">
          <LoginAlertIcon />
          {error}
        </p>
      )}
    </div>
  )
})

export interface LoginSubmitProps {
  loading?: boolean | undefined
  /** Texto durante o carregamento. */
  loadingLabel?: string | undefined
  disabled?: boolean | undefined
  children: ReactNode
}

export function LoginSubmit({ loading = false, loadingLabel = 'Entrando…', disabled, children }: LoginSubmitProps) {
  return (
    <button type="submit" className="ds-login__submit" disabled={loading || disabled} aria-busy={loading}>
      <span>{loading ? loadingLabel : children}</span>
      {loading ? <span className="ds-login__spinner" aria-hidden="true" /> : <ArrowIcon />}
    </button>
  )
}

export interface LoginHelpProps {
  icon?: ReactNode
  title: ReactNode
  children?: ReactNode
}

export function LoginHelp({ icon, title, children }: LoginHelpProps) {
  return (
    <div className="ds-login__help">
      {icon && (
        <span className="ds-login__help-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <div>
        <strong className="ds-login__help-title">{title}</strong>
        {children}
      </div>
    </div>
  )
}

/* ---------- força da senha ---------- */

const FORCA_PADRAO = ['Muito fraca', 'Fraca', 'Razoável', 'Forte', 'Muito forte']

export interface LoginStrengthProps {
  /** Pontuação 0–4 (ex.: zxcvbn(senha).score). */
  score: number
  labels?: string[] | undefined
}

/** Medidor de força de senha: 4 barras (perigo → aviso → sucesso) + rótulo anunciado. */
export function LoginStrength({ score, labels = FORCA_PADRAO }: LoginStrengthProps) {
  const nivel = score <= 1 ? 'danger' : score === 2 ? 'warning' : 'success'
  return (
    <div className={`ds-login__strength ds-login__strength--${nivel}`}>
      <div className="ds-login__strength-bars" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className={i <= score - 1 ? 'is-on' : undefined} />
        ))}
      </div>
      <p className="ds-login__strength-label" aria-live="polite">
        Força da senha: {labels[score] ?? ''}
      </p>
    </div>
  )
}
